import api from "./api"

/**
 * Travel Desk — the trip ROSTER (ArtDent 260914).
 *
 * One row per trip over a date window: the partner who referred the patient,
 * the patient, both flights with their times, the driver on each airport leg,
 * and whether the clinic visit is booked. The board one coordinator works when
 * a clinic divides the desk between several of them.
 *
 * 🟥 A ROSTER, not a worklist. `/booking-worklist` answers "who needs me to
 * act" and drops a row the moment the action is done; this answers "who is
 * travelling", so a booked patient with both rides claimed is still on it. Do
 * not fold the two together — the reason they page and filter differently is
 * that they are different questions.
 *
 * Access: `feature:travel_desk_enabled` (404 when off) AND `travel_desk.view`
 * (403 without). That permission is granted to NO role and handed out per user
 * (`php artisan travel-desk:access <email>`), which is the whole feature — one
 * coordinator out of several gets the board and the rest keep their day.
 *
 * Types are declared HERE (not in `@/types`) so the module is self-contained —
 * same convention as `logistics-api.ts` / `rooms-api.ts`.
 */

/** A transfer's lifecycle, mirrored from the logistics board. */
export type TravelDeskTransferStatus =
  | "unassigned"
  | "assigned"
  | "completed"
  | "no_show"
  | "cancelled"

/**
 * One airport leg.
 *
 * 🟥 `null` for the whole object and `driver: null` are DIFFERENT states, and
 * the table renders them differently: no object means no ride was ever created
 * for that flight, while a null driver means the run exists and is sitting in
 * the pool waiting for one of the drivers to claim it. Collapsing the two would
 * hide the only thing this clinic's pull model needs watching.
 */
export interface TravelDeskLeg {
  status: TravelDeskTransferStatus
  driver: { id: string; name: string } | null
}

export interface TravelDeskRow {
  travel_id: string
  lead_id: string
  patient_name: string
  /**
   * The referring partner, or null for a patient who came to the clinic
   * directly. Only the partner PORTAL writes `affiliator_id` — a referral the
   * desk typed in by hand carries none — so an empty cell means "no partner
   * recorded", never "this patient has no partner".
   */
  affiliator: { id: number; name: string } | null
  arrival_date: string | null
  /** `HH:mm`, and frequently absent — a trip can be booked before the flight is. */
  arrival_time: string | null
  arrival_airport: string | null
  departure_date: string | null
  departure_time: string | null
  departure_airport: string | null
  hotel_name: string | null
  arrival_transfer: TravelDeskLeg | null
  departure_transfer: TravelDeskLeg | null
  /** Either airport leg exists but nobody has claimed it. */
  has_unclaimed_leg: boolean
  /** A clinic visit is on the calendar on or after this trip's arrival date. */
  booked: boolean
}

export interface TravelDeskMeta {
  current_page: number
  last_page: number
  per_page: number
  total: number
}

export interface TravelDeskResponse {
  data: TravelDeskRow[]
  meta: TravelDeskMeta
}

export interface TravelDeskParams {
  /** `yyyy-MM-dd`. Absent, the server uses today. */
  from?: string
  /** `yyyy-MM-dd`. Absent, the server uses `from` + 30 days. */
  to?: string
  /** Filter on the booking state; omit to keep both. */
  booked?: boolean
  /** Only trips with an airport leg nobody has taken. */
  unclaimed?: boolean
  page?: number
}

export async function getTravelDesk(
  params: TravelDeskParams = {},
): Promise<TravelDeskResponse> {
  const query: Record<string, string | number> = {}

  if (params.from) query.from = params.from
  if (params.to) query.to = params.to
  // Explicit undefined check: `false` is a real filter ("show me who is NOT
  // booked"), and a truthiness test would silently drop it.
  if (params.booked !== undefined) query.booked = params.booked ? 1 : 0
  if (params.unclaimed) query.unclaimed = 1
  if (params.page) query.page = params.page

  const { data } = await api.get<TravelDeskResponse>("/travel-desk", {
    params: query,
  })

  return data
}
